import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import "../../styles/StatusHistoryPage.css";
import StatusChangeModal from "../../components/StatusChangeModal";
import PositionHistoryModal from "../../components/PositionHistoryModal";

const StatusHistoryPage = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [user, setUser] = useState(null);
  const [statusList, setStatusList] = useState([]);
  const [history, setHistory] = useState([]);
  const [chainInfo, setChainInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posHistory, setPosHistory] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [targetStatusLabel, setTargetStatusLabel] = useState("");

  const [posModalOpen, setPosModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // 🧠 Fetch combined status + position history
  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      // 🧩 Fetch both history types in parallel
      const [statusRes, positionRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/status-history/${userId}`, { headers }),
        axios.get(`${BASE_URL}/api/status-history/position/${userId}`, {
          headers,
        }),
      ]);

      // ✅ Store results in state
      setHistory(statusRes.data.history || []);
      setChainInfo(statusRes.data);
      setPosHistory(positionRes.data || []);
    } catch (err) {
      console.error("❌ Failed to load history data:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, BASE_URL]);

  // 🧠 Fetch user & status list
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const [userRes, statusRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/users/${userId}`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
        ]);
        setUser(userRes.data);
        setStatusList(statusRes.data);
      } catch (err) {
        console.error("❌ Failed to fetch user data:", err);
      }
    };
    fetchUserData();
  }, [userId, BASE_URL]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const getStatusName = (id) => {
    return (
      statusList.find((s) => s.s_id === Number(id))?.s_TranslatedText ||
      statusList.find((s) => s.s_id === Number(id))?.s_name ||
      "-"
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <h4>Status History</h4>
        <div>
          <button
            className="btn btn-outline-primary me-2"
            onClick={() => navigate(`/application-history/${userId}`)}
          >
            📄 Application History
          </button>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        {chainInfo?.allIds?.length > 1 && (
          <p className="text-muted small mb-3">
            Showing combined history for linked users:{" "}
            <strong>{chainInfo.allIds.join(", ")}</strong>
          </p>
        )}

        {user && (
          <div className="card shadow-sm">
            <div className="d-flex justify-content-between align-items-center p-3 pb-0">
              <h5>Status</h5>
            </div>
            <div className="card-body">
              <div className="status-block">
                <div className="status-line">
                  <span className="status-label">Status:</span>
                  <span className="status-chip">
                    {getStatusName(user.status)}
                  </span>
                </div>

                <div className="move-to">
                  <span className="move-to-label">Move To:</span>
                  {[
                    "Associate",
                    "Candidate",
                    "Future Candidate",
                    "Prospect",
                    "Inactive",
                  ].map((label) => {
                    const match = statusList.find(
                      (s) => (s.s_TranslatedText || "").trim() === label
                    );
                    if (!match || match.s_id === Number(user.status))
                      return null;

                    return (
                      <button
                        key={label}
                        type="button"
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={() => {
                          setTargetStatus(match.s_id);
                          setTargetStatusLabel(label);
                          setModalOpen(true);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {user && (
          <div className="card shadow-sm">
            <div className="d-flex justify-content-between align-items-center p-3 pb-0">
              <h5>Key Dates</h5>
            </div>
            <div className="card-body keydates-section">
              <div className="keydates-grid">
                <div className="keydates-item">
                  <strong>Created</strong>
                  <div className="keydates-value">
                    {chainInfo?.metaDates?.createdDate
                      ? dayjs(chainInfo.metaDates.createdDate).format(
                          "MM/DD/YYYY"
                        )
                      : "-"}
                  </div>
                </div>

                <div className="keydates-item">
                  <strong>Employment Start</strong>
                  <div className="keydates-value">
                    {chainInfo?.metaDates?.employmentStart
                      ? dayjs(chainInfo.metaDates.employmentStart).format(
                          "MM/DD/YYYY"
                        )
                      : "-"}
                  </div>
                </div>

                <div className="keydates-item">
                  <strong>Employment End</strong>
                  <div className="keydates-value">
                    {user?.user_terminate_date
                      ? dayjs(user.user_terminate_date).format("MM/DD/YYYY")
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5>Status History</h5>
          </div>
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-secondary" role="status" />
              <p className="mt-2 text-muted">Loading status history...</p>
            </div>
          ) : history.length === 0 ? (
            <p>No status history found.</p>
          ) : (
            <table className="table table-striped align-middle mt-3">
              <thead>
                <tr>
                  <th>Date Changed</th>
                  <th>Change From</th>
                  <th>Change To</th>
                  <th>Reason</th>
                  <th>Effective Date</th>
                  <th>Changed By</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.sh_id}>
                    <td>{dayjs(h.sh_changed_at).format("MM/DD/YYYY HH:mm")}</td>
                    <td>
                      {h.old_status_name || getStatusName(h.sh_old_status)}
                    </td>
                    <td>
                      {h.new_status_name || getStatusName(h.sh_new_status)}
                    </td>
                    <td>{h.reason_name || "-"}</td>
                    <td>
                      {h.sh_effective_date
                        ? dayjs(h.sh_effective_date).format("MM/DD/YYYY")
                        : "-"}
                    </td>
                    <td>{h.changed_by_name || "-"}</td>
                    <td>{h.sh_note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card shadow-sm p-3 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5>Position History</h5>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingRecord(null);
                setPosModalOpen(true);
              }}
            >
              + Add New
            </button>
          </div>
          {posHistory.length === 0 ? (
            <p>No position history found.</p>
          ) : (
            <table className="table table-striped align-middle mt-3">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Emp Category</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Reason for Change</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {posHistory.map((p) => (
                  <tr
                    key={p.ph_id}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setEditingRecord(p);
                      setPosModalOpen(true);
                    }}
                  >
                    <td style={{ whiteSpace: "pre-line" }}>
                      {p.ph_position_text || "-"}
                    </td>
                    <td>{p.emp_category_name || "-"}</td>
                    <td>
                      {p.ph_start_date
                        ? dayjs(p.ph_start_date).format("MM/DD/YYYY")
                        : "-"}
                    </td>
                    <td>
                      {p.ph_end_date
                        ? dayjs(p.ph_end_date).format("MM/DD/YYYY")
                        : "-"}
                    </td>
                    <td>{p.ph_reason_for_change || "-"}</td>
                    <td>{p.ph_note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {user && (
        <>
          {/* Status Change Modal */}
          <StatusChangeModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            userId={user.id}
            currentStatus={user.status}
            newStatus={targetStatus}
            newStatusLabel={targetStatusLabel}
            onSuccess={async () => {
              try {
                const headers = {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                };
                const res = await axios.get(`${BASE_URL}/api/users/${userId}`, {
                  headers,
                });
                setUser(res.data);

                // ✅ Refresh position & status history after status change
                await fetchHistory();

                alert("✔️ Status updated successfully");

                // ✅ Optional redirect
                navigate("/admin");
              } catch (err) {
                console.error("Failed to refresh user:", err);
                alert("⚠️ Status updated but redirect failed.");
              }
            }}
          />

          {/* 🆕 Position History Modal */}
          <PositionHistoryModal
            isOpen={posModalOpen}
            onClose={() => setPosModalOpen(false)}
            record={editingRecord}
            userId={userId}
            BASE_URL={BASE_URL}
            onSave={fetchHistory} // refresh table after save/edit
          />
        </>
      )}
    </>
  );
};

export default StatusHistoryPage;
